import AsyncStorage from '@react-native-async-storage/async-storage';
import { Alert } from 'react-native';

/**
 * Unified API Service for Business Operations
 * Provides consistent API calls with error handling and offline support
 */

class BusinessApiService {
  constructor() {
   this.baseURL = 'https://api.jobeng.ai';

    this.timeout = 30000; // 30 seconds
    this.retryAttempts = 3;
    this.retryDelay = 1000; // 1 second
    this.offlineQueue = [];
    this.isOnline = true;
  }

  // Get authentication headers
  async getAuthHeaders() {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      return {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      };
    } catch (error) {
      console.error('❌ Error getting auth headers:', error);
      return {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };
    }
  }

  // Generic API request with retry logic
  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = await this.getAuthHeaders();
    
    const requestOptions = {
      timeout: this.timeout,
      headers: {
        ...headers,
        ...options.headers,
      },
      ...options,
    };

    for (let attempt = 1; attempt <= this.retryAttempts; attempt++) {
      try {
        console.log(`🔄 API Request (Attempt ${attempt}): ${options.method || 'GET'} ${url}`);
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);
        
        const response = await fetch(url, {
          ...requestOptions,
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const responseText = await response.text();
        let data;
        
        try {
          data = JSON.parse(responseText);
        } catch (parseError) {
          console.warn('⚠️ Non-JSON response received:', responseText);
          data = { message: responseText };
        }
        
        console.log(`✅ API Success: ${options.method || 'GET'} ${url}`);
        this.isOnline = true;
        
        // Process offline queue if we're back online
        if (this.offlineQueue.length > 0) {
          this.processOfflineQueue();
        }
        
        return data;
        
      } catch (error) {
        console.error(`❌ API Error (Attempt ${attempt}): ${error.message}`);
        
        if (attempt === this.retryAttempts) {
          // Last attempt failed
          if (error.name === 'AbortError' || error.message.includes('timeout')) {
            throw new Error('Request timeout. Please check your connection.');
          } else if (error.message.includes('Network request failed')) {
            this.isOnline = false;
            throw new Error('Network error. Please check your internet connection.');
          } else {
            throw error;
          }
        }
        
        // Wait before retry
        await this.delay(this.retryDelay * attempt);
      }
    }
  }

  // Delay utility for retries
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Add request to offline queue
  addToOfflineQueue(endpoint, options) {
    this.offlineQueue.push({
      endpoint,
      options,
      timestamp: new Date().toISOString(),
    });
    console.log(`📱 Added to offline queue: ${options.method || 'GET'} ${endpoint}`);
  }

  // Process offline queue when back online
  async processOfflineQueue() {
    console.log(`🔄 Processing ${this.offlineQueue.length} offline requests...`);
    
    const queue = [...this.offlineQueue];
    this.offlineQueue = [];
    
    for (const request of queue) {
      try {
        await this.makeRequest(request.endpoint, request.options);
        console.log(`✅ Offline request processed: ${request.options.method || 'GET'} ${request.endpoint}`);
      } catch (error) {
        console.error(`❌ Failed to process offline request: ${error.message}`);
        // Re-add to queue if still failing
        this.offlineQueue.push(request);
      }
    }
  }

  // Handle API errors consistently
  handleError(error, context) {
    console.error(`❌ ${context} error:`, error);
    
    let userMessage = 'An unexpected error occurred. Please try again.';
    
    if (error.message.includes('timeout')) {
      userMessage = 'Request timeout. Please check your connection and try again.';
    } else if (error.message.includes('Network')) {
      userMessage = 'Network error. Please check your internet connection.';
    } else if (error.message.includes('401')) {
      userMessage = 'Authentication failed. Please log in again.';
    } else if (error.message.includes('403')) {
      userMessage = 'Access denied. You don\'t have permission for this action.';
    } else if (error.message.includes('404')) {
      userMessage = 'Resource not found.';
    } else if (error.message.includes('500')) {
      userMessage = 'Server error. Please try again later.';
    }
    
    return {
      error: true,
      message: userMessage,
      originalError: error.message,
      context,
    };
  }

  // Jobs API
  jobs = {
    getAll: async () => {
      try {
        const response = await this.makeRequest('/business/jobs');
        return response.data || response.jobs || [];
      } catch (error) {
        if (!this.isOnline) {
          // Return cached data if offline
          const cached = await AsyncStorage.getItem('cached_jobs');
          return cached ? JSON.parse(cached) : [];
        }
        throw this.handleError(error, 'fetching jobs');
      }
    },

    getById: async (id) => {
      try {
        const response = await this.makeRequest(`/business/jobs/${id}`);
        return response.data || response.job || null;
      } catch (error) {
        throw this.handleError(error, 'fetching job');
      }
    },

    create: async (jobData) => {
      try {
        const response = await this.makeRequest('/business/jobs', {
          method: 'POST',
          body: JSON.stringify(jobData),
        });
        return response.data || response.job || null;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue('/business/jobs', {
            method: 'POST',
            body: JSON.stringify(jobData),
          });
          return { ...jobData, id: 'temp_' + Date.now(), status: 'pending' };
        }
        throw this.handleError(error, 'creating job');
      }
    },

    update: async (id, jobData) => {
      try {
        const response = await this.makeRequest(`/business/jobs/${id}`, {
          method: 'PUT',
          body: JSON.stringify(jobData),
        });
        return response.data || response.job || null;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue(`/business/jobs/${id}`, {
            method: 'PUT',
            body: JSON.stringify(jobData),
          });
          return { ...jobData, id, status: 'pending' };
        }
        throw this.handleError(error, 'updating job');
      }
    },

    delete: async (id) => {
      try {
        const response = await this.makeRequest(`/business/jobs/${id}`, {
          method: 'DELETE',
        });
        return response.success || true;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue(`/business/jobs/${id}`, {
            method: 'DELETE',
          });
          return true;
        }
        throw this.handleError(error, 'deleting job');
      }
    },

    getApplications: async (jobId) => {
      try {
        const response = await this.makeRequest(`/business/jobs/${jobId}/applications`);
        return response.data || response.applications || [];
      } catch (error) {
        throw this.handleError(error, 'fetching job applications');
      }
    },

    getStats: async () => {
      try {
        const response = await this.makeRequest('/business/jobs/stats');
        return response.data || response.stats || {};
      } catch (error) {
        throw this.handleError(error, 'fetching job stats');
      }
    },
  };

  // Campaigns API
  campaigns = {
    getAll: async () => {
      try {
        const response = await this.makeRequest('/business/campaigns');
        return response.data || response.campaigns || [];
      } catch (error) {
        if (!this.isOnline) {
          const cached = await AsyncStorage.getItem('cached_campaigns');
          return cached ? JSON.parse(cached) : [];
        }
        throw this.handleError(error, 'fetching campaigns');
      }
    },

    getById: async (id) => {
      try {
        const response = await this.makeRequest(`/business/campaigns/${id}`);
        return response.data || response.campaign || null;
      } catch (error) {
        throw this.handleError(error, 'fetching campaign');
      }
    },

    create: async (campaignData) => {
      try {
        const response = await this.makeRequest('/business/campaigns', {
          method: 'POST',
          body: JSON.stringify(campaignData),
        });
        return response.data || response.campaign || null;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue('/business/campaigns', {
            method: 'POST',
            body: JSON.stringify(campaignData),
          });
          return { ...campaignData, id: 'temp_' + Date.now(), status: 'pending' };
        }
        throw this.handleError(error, 'creating campaign');
      }
    },

    update: async (id, campaignData) => {
      try {
        const response = await this.makeRequest(`/business/campaigns/${id}`, {
          method: 'PUT',
          body: JSON.stringify(campaignData),
        });
        return response.data || response.campaign || null;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue(`/business/campaigns/${id}`, {
            method: 'PUT',
            body: JSON.stringify(campaignData),
          });
          return { ...campaignData, id, status: 'pending' };
        }
        throw this.handleError(error, 'updating campaign');
      }
    },

    delete: async (id) => {
      try {
        const response = await this.makeRequest(`/business/campaigns/${id}`, {
          method: 'DELETE',
        });
        return response.success || true;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue(`/business/campaigns/${id}`, {
            method: 'DELETE',
          });
          return true;
        }
        throw this.handleError(error, 'deleting campaign');
      }
    },

    getAnalytics: async (id) => {
      try {
        const response = await this.makeRequest(`/business/campaigns/${id}/analytics`);
        return response.data || response.analytics || {};
      } catch (error) {
        throw this.handleError(error, 'fetching campaign analytics');
      }
    },

    getStats: async () => {
      try {
        const response = await this.makeRequest('/business/campaigns/stats');
        return response.data || response.stats || {};
      } catch (error) {
        throw this.handleError(error, 'fetching campaign stats');
      }
    },
  };

  // Projects API
  projects = {
    getAll: async () => {
      try {
        const response = await this.makeRequest('/business/projects');
        return response.data || response.projects || [];
      } catch (error) {
        if (!this.isOnline) {
          const cached = await AsyncStorage.getItem('cached_projects');
          return cached ? JSON.parse(cached) : [];
        }
        throw this.handleError(error, 'fetching projects');
      }
    },

    getById: async (id) => {
      try {
        const response = await this.makeRequest(`/business/projects/${id}`);
        return response.data || response.project || null;
      } catch (error) {
        throw this.handleError(error, 'fetching project');
      }
    },

    create: async (projectData) => {
      try {
        const response = await this.makeRequest('/business/projects', {
          method: 'POST',
          body: JSON.stringify(projectData),
        });
        return response.data || response.project || null;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue('/business/projects', {
            method: 'POST',
            body: JSON.stringify(projectData),
          });
          return { ...projectData, id: 'temp_' + Date.now(), status: 'pending' };
        }
        throw this.handleError(error, 'creating project');
      }
    },

    update: async (id, projectData) => {
      try {
        const response = await this.makeRequest(`/business/projects/${id}`, {
          method: 'PUT',
          body: JSON.stringify(projectData),
        });
        return response.data || response.project || null;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue(`/business/projects/${id}`, {
            method: 'PUT',
            body: JSON.stringify(projectData),
          });
          return { ...projectData, id, status: 'pending' };
        }
        throw this.handleError(error, 'updating project');
      }
    },

    delete: async (id) => {
      try {
        const response = await this.makeRequest(`/business/projects/${id}`, {
          method: 'DELETE',
        });
        return response.success || true;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue(`/business/projects/${id}`, {
            method: 'DELETE',
          });
          return true;
        }
        throw this.handleError(error, 'deleting project');
      }
    },

    getTeam: async (id) => {
      try {
        const response = await this.makeRequest(`/business/projects/${id}/team`);
        return response.data || response.team || [];
      } catch (error) {
        throw this.handleError(error, 'fetching project team');
      }
    },

    getMilestones: async (id) => {
      try {
        const response = await this.makeRequest(`/business/projects/${id}/milestones`);
        return response.data || response.milestones || [];
      } catch (error) {
        throw this.handleError(error, 'fetching project milestones');
      }
    },

    getAnalytics: async (id) => {
      try {
        const response = await this.makeRequest(`/business/projects/${id}/analytics`);
        return response.data || response.analytics || {};
      } catch (error) {
        throw this.handleError(error, 'fetching project analytics');
      }
    },

    getStats: async () => {
      try {
        const response = await this.makeRequest('/business/projects/stats');
        return response.data || response.stats || {};
      } catch (error) {
        throw this.handleError(error, 'fetching project stats');
      }
    },
  };

  // Applicants API
  applicants = {
    getAll: async () => {
      try {
        const response = await this.makeRequest('/business/applicants');
        return response.data || response.applicants || [];
      } catch (error) {
        if (!this.isOnline) {
          const cached = await AsyncStorage.getItem('cached_applicants');
          return cached ? JSON.parse(cached) : [];
        }
        throw this.handleError(error, 'fetching applicants');
      }
    },

    getById: async (id) => {
      try {
        const response = await this.makeRequest(`/business/applicants/${id}`);
        return response.data || response.applicant || null;
      } catch (error) {
        throw this.handleError(error, 'fetching applicant');
      }
    },

    getByJob: async (jobId) => {
      try {
        const response = await this.makeRequest(`/business/jobs/${jobId}/applicants`);
        return response.data || response.applicants || [];
      } catch (error) {
        throw this.handleError(error, 'fetching job applicants');
      }
    },

    updateStatus: async (id, status) => {
      try {
        const response = await this.makeRequest(`/business/applicants/${id}/status`, {
          method: 'PUT',
          body: JSON.stringify({ status }),
        });
        return response.data || response.applicant || null;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue(`/business/applicants/${id}/status`, {
            method: 'PUT',
            body: JSON.stringify({ status }),
          });
          return { id, status, updated: true };
        }
        throw this.handleError(error, 'updating applicant status');
      }
    },

    getStats: async () => {
      try {
        const response = await this.makeRequest('/business/applicants/stats');
        return response.data || response.stats || {};
      } catch (error) {
        throw this.handleError(error, 'fetching applicant stats');
      }
    },
  };

  // Interviews API
  interviews = {
    getAll: async () => {
      try {
        const response = await this.makeRequest('/business/interviews');
        return response.data || response.interviews || [];
      } catch (error) {
        if (!this.isOnline) {
          const cached = await AsyncStorage.getItem('cached_interviews');
          return cached ? JSON.parse(cached) : [];
        }
        throw this.handleError(error, 'fetching interviews');
      }
    },

    getById: async (id) => {
      try {
        const response = await this.makeRequest(`/business/interviews/${id}`);
        return response.data || response.interview || null;
      } catch (error) {
        throw this.handleError(error, 'fetching interview');
      }
    },

    schedule: async (interviewData) => {
      try {
        const response = await this.makeRequest('/business/interviews', {
          method: 'POST',
          body: JSON.stringify(interviewData),
        });
        return response.data || response.interview || null;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue('/business/interviews', {
            method: 'POST',
            body: JSON.stringify(interviewData),
          });
          return { ...interviewData, id: 'temp_' + Date.now(), status: 'pending' };
        }
        throw this.handleError(error, 'scheduling interview');
      }
    },

    reschedule: async (id, newDateTime) => {
      try {
        const response = await this.makeRequest(`/business/interviews/${id}/reschedule`, {
          method: 'PUT',
          body: JSON.stringify({ scheduledDate: newDateTime }),
        });
        return response.data || response.interview || null;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue(`/business/interviews/${id}/reschedule`, {
            method: 'PUT',
            body: JSON.stringify({ scheduledDate: newDateTime }),
          });
          return { id, scheduledDate: newDateTime, status: 'rescheduled' };
        }
        throw this.handleError(error, 'rescheduling interview');
      }
    },

    cancel: async (id) => {
      try {
        const response = await this.makeRequest(`/business/interviews/${id}`, {
          method: 'DELETE',
        });
        return response.success || true;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue(`/business/interviews/${id}`, {
            method: 'DELETE',
          });
          return true;
        }
        throw this.handleError(error, 'canceling interview');
      }
    },

    getStats: async () => {
      try {
        const response = await this.makeRequest('/business/interviews/stats');
        return response.data || response.stats || {};
      } catch (error) {
        throw this.handleError(error, 'fetching interview stats');
      }
    },
  };

  // Financial API
  financial = {
    getInvoices: async () => {
      try {
        const response = await this.makeRequest('/business/financial/invoices');
        return response.data || response.invoices || [];
      } catch (error) {
        if (!this.isOnline) {
          const cached = await AsyncStorage.getItem('cached_invoices');
          return cached ? JSON.parse(cached) : [];
        }
        throw this.handleError(error, 'fetching invoices');
      }
    },

    createInvoice: async (invoiceData) => {
      try {
        const response = await this.makeRequest('/business/financial/invoices', {
          method: 'POST',
          body: JSON.stringify(invoiceData),
        });
        return response.data || response.invoice || null;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue('/business/financial/invoices', {
            method: 'POST',
            body: JSON.stringify(invoiceData),
          });
          return { ...invoiceData, id: 'temp_' + Date.now(), status: 'pending' };
        }
        throw this.handleError(error, 'creating invoice');
      }
    },

    updateInvoice: async (id, invoiceData) => {
      try {
        const response = await this.makeRequest(`/business/financial/invoices/${id}`, {
          method: 'PUT',
          body: JSON.stringify(invoiceData),
        });
        return response.data || response.invoice || null;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue(`/business/financial/invoices/${id}`, {
            method: 'PUT',
            body: JSON.stringify(invoiceData),
          });
          return { ...invoiceData, id, status: 'pending' };
        }
        throw this.handleError(error, 'updating invoice');
      }
    },

    getContracts: async () => {
      try {
        const response = await this.makeRequest('/business/financial/contracts');
        return response.data || response.contracts || [];
      } catch (error) {
        if (!this.isOnline) {
          const cached = await AsyncStorage.getItem('cached_contracts');
          return cached ? JSON.parse(cached) : [];
        }
        throw this.handleError(error, 'fetching contracts');
      }
    },

    createContract: async (contractData) => {
      try {
        const response = await this.makeRequest('/business/financial/contracts', {
          method: 'POST',
          body: JSON.stringify(contractData),
        });
        return response.data || response.contract || null;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue('/business/financial/contracts', {
            method: 'POST',
            body: JSON.stringify(contractData),
          });
          return { ...contractData, id: 'temp_' + Date.now(), status: 'pending' };
        }
        throw this.handleError(error, 'creating contract');
      }
    },

    getPayments: async () => {
      try {
        const response = await this.makeRequest('/business/financial/payments');
        return response.data || response.payments || [];
      } catch (error) {
        if (!this.isOnline) {
          const cached = await AsyncStorage.getItem('cached_payments');
          return cached ? JSON.parse(cached) : [];
        }
        throw this.handleError(error, 'fetching payments');
      }
    },

    processPayment: async (paymentData) => {
      try {
        const response = await this.makeRequest('/business/financial/payments', {
          method: 'POST',
          body: JSON.stringify(paymentData),
        });
        return response.data || response.payment || null;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue('/business/financial/payments', {
            method: 'POST',
            body: JSON.stringify(paymentData),
          });
          return { ...paymentData, id: 'temp_' + Date.now(), status: 'pending' };
        }
        throw this.handleError(error, 'processing payment');
      }
    },

    getStats: async () => {
      try {
        const response = await this.makeRequest('/business/financial/stats');
        return response.data || response.stats || {};
      } catch (error) {
        throw this.handleError(error, 'fetching financial stats');
      }
    },
  };

  // Compliance API
  compliance = {
    getItems: async () => {
      try {
        const response = await this.makeRequest('/business/compliance/items');
        return response.data || response.items || [];
      } catch (error) {
        if (!this.isOnline) {
          const cached = await AsyncStorage.getItem('cached_compliance');
          return cached ? JSON.parse(cached) : [];
        }
        throw this.handleError(error, 'fetching compliance items');
      }
    },

    createItem: async (itemData) => {
      try {
        const response = await this.makeRequest('/business/compliance/items', {
          method: 'POST',
          body: JSON.stringify(itemData),
        });
        return response.data || response.item || null;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue('/business/compliance/items', {
            method: 'POST',
            body: JSON.stringify(itemData),
          });
          return { ...itemData, id: 'temp_' + Date.now(), status: 'pending' };
        }
        throw this.handleError(error, 'creating compliance item');
      }
    },

    updateItem: async (id, itemData) => {
      try {
        const response = await this.makeRequest(`/business/compliance/items/${id}`, {
          method: 'PUT',
          body: JSON.stringify(itemData),
        });
        return response.data || response.item || null;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue(`/business/compliance/items/${id}`, {
            method: 'PUT',
            body: JSON.stringify(itemData),
          });
          return { ...itemData, id, status: 'pending' };
        }
        throw this.handleError(error, 'updating compliance item');
      }
    },

    getRegulatoryUpdates: async () => {
      try {
        const response = await this.makeRequest('/business/compliance/regulatory');
        return response.data || response.updates || [];
      } catch (error) {
        if (!this.isOnline) {
          const cached = await AsyncStorage.getItem('cached_regulatory');
          return cached ? JSON.parse(cached) : [];
        }
        throw this.handleError(error, 'fetching regulatory updates');
      }
    },

    getDeadlines: async () => {
      try {
        const response = await this.makeRequest('/business/compliance/deadlines');
        return response.data || response.deadlines || [];
      } catch (error) {
        if (!this.isOnline) {
          const cached = await AsyncStorage.getItem('cached_deadlines');
          return cached ? JSON.parse(cached) : [];
        }
        throw this.handleError(error, 'fetching compliance deadlines');
      }
    },

    generateReport: async (reportType, dateRange) => {
      try {
        const response = await this.makeRequest('/business/compliance/reports', {
          method: 'POST',
          body: JSON.stringify({ reportType, dateRange }),
        });
        return response.data || response.report || null;
      } catch (error) {
        throw this.handleError(error, 'generating compliance report');
      }
    },

    getStats: async () => {
      try {
        const response = await this.makeRequest('/business/compliance/stats');
        return response.data || response.stats || {};
      } catch (error) {
        throw this.handleError(error, 'fetching compliance stats');
      }
    },
  };

  // Vendors API
  vendors = {
    getAll: async () => {
      try {
        const response = await this.makeRequest('/business/vendors');
        return response.data || response.vendors || [];
      } catch (error) {
        if (!this.isOnline) {
          const cached = await AsyncStorage.getItem('cached_vendors');
          return cached ? JSON.parse(cached) : [];
        }
        throw this.handleError(error, 'fetching vendors');
      }
    },

    getById: async (id) => {
      try {
        const response = await this.makeRequest(`/business/vendors/${id}`);
        return response.data || response.vendor || null;
      } catch (error) {
        throw this.handleError(error, 'fetching vendor');
      }
    },

    create: async (vendorData) => {
      try {
        const response = await this.makeRequest('/business/vendors', {
          method: 'POST',
          body: JSON.stringify(vendorData),
        });
        return response.data || response.vendor || null;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue('/business/vendors', {
            method: 'POST',
            body: JSON.stringify(vendorData),
          });
          return { ...vendorData, id: 'temp_' + Date.now(), status: 'pending' };
        }
        throw this.handleError(error, 'creating vendor');
      }
    },

    update: async (id, vendorData) => {
      try {
        const response = await this.makeRequest(`/business/vendors/${id}`, {
          method: 'PUT',
          body: JSON.stringify(vendorData),
        });
        return response.data || response.vendor || null;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue(`/business/vendors/${id}`, {
            method: 'PUT',
            body: JSON.stringify(vendorData),
          });
          return { ...vendorData, id, status: 'pending' };
        }
        throw this.handleError(error, 'updating vendor');
      }
    },

    delete: async (id) => {
      try {
        const response = await this.makeRequest(`/business/vendors/${id}`, {
          method: 'DELETE',
        });
        return response.success || true;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue(`/business/vendors/${id}`, {
            method: 'DELETE',
          });
          return true;
        }
        throw this.handleError(error, 'deleting vendor');
      }
    },

    getSubcontractors: async () => {
      try {
        const response = await this.makeRequest('/business/subcontractors');
        return response.data || response.subcontractors || [];
      } catch (error) {
        if (!this.isOnline) {
          const cached = await AsyncStorage.getItem('cached_subcontractors');
          return cached ? JSON.parse(cached) : [];
        }
        throw this.handleError(error, 'fetching subcontractors');
      }
    },

    createSubcontractor: async (subcontractorData) => {
      try {
        const response = await this.makeRequest('/business/subcontractors', {
          method: 'POST',
          body: JSON.stringify(subcontractorData),
        });
        return response.data || response.subcontractor || null;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue('/business/subcontractors', {
            method: 'POST',
            body: JSON.stringify(subcontractorData),
          });
          return { ...subcontractorData, id: 'temp_' + Date.now(), status: 'pending' };
        }
        throw this.handleError(error, 'creating subcontractor');
      }
    },

    getStats: async () => {
      try {
        const response = await this.makeRequest('/business/vendors/stats');
        return response.data || response.stats || {};
      } catch (error) {
        throw this.handleError(error, 'fetching vendor stats');
      }
    },
  };

  // Notifications API
  notifications = {
    getAll: async () => {
      try {
        const response = await this.makeRequest('/business/notifications');
        return response.data || response.notifications || [];
      } catch (error) {
        if (!this.isOnline) {
          const cached = await AsyncStorage.getItem('cached_notifications');
          return cached ? JSON.parse(cached) : [];
        }
        throw this.handleError(error, 'fetching notifications');
      }
    },

    markAsRead: async (id) => {
      try {
        const response = await this.makeRequest(`/business/notifications/${id}/read`, {
          method: 'PUT',
        });
        return response.success || true;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue(`/business/notifications/${id}/read`, {
            method: 'PUT',
          });
          return true;
        }
        throw this.handleError(error, 'marking notification as read');
      }
    },

    markAllAsRead: async () => {
      try {
        const response = await this.makeRequest('/business/notifications/read-all', {
          method: 'PUT',
        });
        return response.success || true;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue('/business/notifications/read-all', {
            method: 'PUT',
          });
          return true;
        }
        throw this.handleError(error, 'marking all notifications as read');
      }
    },

    delete: async (id) => {
      try {
        const response = await this.makeRequest(`/business/notifications/${id}`, {
          method: 'DELETE',
        });
        return response.success || true;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue(`/business/notifications/${id}`, {
            method: 'DELETE',
          });
          return true;
        }
        throw this.handleError(error, 'deleting notification');
      }
    },

    create: async (notificationData) => {
      try {
        const response = await this.makeRequest('/business/notifications', {
          method: 'POST',
          body: JSON.stringify(notificationData),
        });
        return response.data || response.notification || null;
      } catch (error) {
        if (!this.isOnline) {
          this.addToOfflineQueue('/business/notifications', {
            method: 'POST',
            body: JSON.stringify(notificationData),
          });
          return { ...notificationData, id: 'temp_' + Date.now(), status: 'pending' };
        }
        throw this.handleError(error, 'creating notification');
      }
    },
  };

  // Analytics API
  analytics = {
    getDashboard: async () => {
      try {
        const response = await this.makeRequest('/business/analytics/dashboard');
        return response.data || response.analytics || {};
      } catch (error) {
        throw this.handleError(error, 'fetching dashboard analytics');
      }
    },

    getJobAnalytics: async (dateRange) => {
      try {
        const response = await this.makeRequest(`/business/analytics/jobs?range=${dateRange}`);
        return response.data || response.analytics || {};
      } catch (error) {
        throw this.handleError(error, 'fetching job analytics');
      }
    },

    getCampaignAnalytics: async (dateRange) => {
      try {
        const response = await this.makeRequest(`/business/analytics/campaigns?range=${dateRange}`);
        return response.data || response.analytics || {};
      } catch (error) {
        throw this.handleError(error, 'fetching campaign analytics');
      }
    },

    getProjectAnalytics: async (dateRange) => {
      try {
        const response = await this.makeRequest(`/business/analytics/projects?range=${dateRange}`);
        return response.data || response.analytics || {};
      } catch (error) {
        throw this.handleError(error, 'fetching project analytics');
      }
    },

    getFinancialAnalytics: async (dateRange) => {
      try {
        const response = await this.makeRequest(`/business/analytics/financial?range=${dateRange}`);
        return response.data || response.analytics || {};
      } catch (error) {
        throw this.handleError(error, 'fetching financial analytics');
      }
    },
  };

  // Cache management
  cache = {
    set: async (key, data) => {
      try {
        await AsyncStorage.setItem(`cached_${key}`, JSON.stringify(data));
        console.log(`✅ Cached data for ${key}`);
      } catch (error) {
        console.error(`❌ Error caching ${key}:`, error);
      }
    },

    get: async (key) => {
      try {
        const cached = await AsyncStorage.getItem(`cached_${key}`);
        return cached ? JSON.parse(cached) : null;
      } catch (error) {
        console.error(`❌ Error getting cached ${key}:`, error);
        return null;
      }
    },

    clear: async (key) => {
      try {
        await AsyncStorage.removeItem(`cached_${key}`);
        console.log(`✅ Cleared cache for ${key}`);
      } catch (error) {
        console.error(`❌ Error clearing cache for ${key}:`, error);
      }
    },

    clearAll: async () => {
      try {
        const keys = await AsyncStorage.getAllKeys();
        const cacheKeys = keys.filter(key => key.startsWith('cached_'));
        await AsyncStorage.multiRemove(cacheKeys);
        console.log('✅ Cleared all cache');
      } catch (error) {
        console.error('❌ Error clearing all cache:', error);
      }
    },
  };

  // Health check
  healthCheck = async () => {
    try {
      const response = await this.makeRequest('/health');
      return response.status === 'ok';
    } catch (error) {
      console.error('❌ Health check failed:', error);
      return false;
    }
  };
}

// Create singleton instance
const businessApiService = new BusinessApiService();

export default businessApiService;

